// Tracking number series (03 §3.5, D-27): the system generates every tracking number from a
// series: `{PREFIX}{YY}{SEQ}{CHECK}{SUFFIX}`, where the year part and the check digit are optional,
// SEQ has 4 to 9 digits, and the prefix and suffix are up to 8 of A–Z, 0–9 and '-'. Every branch has
// a default series (`{BRANCH}{YY}{SEQ6}{CHECK}`, the 6.0 shape since day one); customers and
// agents may have their own. A tracking number may also be typed by hand (a pre-printed agent
// airway bill): `trackingNoProblem` says whether such a number can be one at all.
// Pure string work: no I/O.
import { luhnCheckDigit } from './ids';

/** Shortest and longest tracking number. The longest fits a piece label (`-P50`) in Code 128 on a 100 × 50 mm label. */
export const TRACKING_NO_MIN_LENGTH = 4;
export const TRACKING_NO_MAX_LENGTH = 20;
/** The most characters in a series prefix or suffix. */
export const SERIES_AFFIX_MAX = 8;
export const SERIES_DIGITS_MIN = 4;
export const SERIES_DIGITS_MAX = 9;

export interface TrackingSeriesFormat {
  /** A–Z, 0–9 and '-', up to 8. */
  readonly prefix: string;
  /** A–Z, 0–9 and '-', up to 8. */
  readonly suffix: string;
  /** How many digits the running number has, 4 to 9. */
  readonly digits: number;
  /** The two-digit year (`26`) before the running number; it restarts each year. */
  readonly yearPart: boolean;
  /** A Luhn check digit over the digits (year and running number), after them. */
  readonly checkDigit: boolean;
}

export interface TrackingSeries extends TrackingSeriesFormat {
  readonly id: string;
}

/** The branch default: `{BRANCH}{YY}{SEQ6}{CHECK}`, identical to every number 6.0 has made so far. */
export function branchDefaultFormat(branchCode: string): TrackingSeriesFormat {
  return { prefix: branchCode, suffix: '', digits: 6, yearPart: true, checkDigit: true };
}

/** The default series of a branch. Its id is the branch code, so its counters keep their names. */
export function branchDefaultSeries(branchCode: string): TrackingSeries {
  return { id: branchCode, ...branchDefaultFormat(branchCode) };
}

const AFFIX = /^[A-Z0-9-]*$/;
const PIECE_END = /-P\d+$/;
const BOX_BARCODE = /^EB\d{10}$/;

/** What staff type, as the system writes it: capitals, no spaces. */
export function normaliseTrackingInput(text: string): string {
  return text.replace(/\s+/g, '').toUpperCase();
}

/**
 * Why `value` cannot be a tracking number, in words, or null when it can. Every tracking number is
 * a Firestore document id and goes on labels and in links, so it is A–Z, 0–9 and '-', 4 to 20
 * long, starts and ends with a letter or digit, has no '--' and at least one digit (so a word is
 * never taken for one). It may not look like a piece
 * label (`…-P1`) or a box barcode (`EB` and 10 digits), so scanners never confuse them.
 */
export function trackingNoProblem(value: string): string | null {
  if (value.length === 0) return 'Type a tracking number.';
  if (!/^[A-Z0-9-]+$/.test(value)) return 'Use capital letters A–Z, digits 0–9 and "-" only.';
  if (value.length < TRACKING_NO_MIN_LENGTH)
    return `A tracking number has at least ${TRACKING_NO_MIN_LENGTH} characters.`;
  if (value.length > TRACKING_NO_MAX_LENGTH)
    return `A tracking number has at most ${TRACKING_NO_MAX_LENGTH} characters, so it fits the labels.`;
  if (value.startsWith('-') || value.endsWith('-'))
    return 'A tracking number starts and ends with a letter or a digit.';
  if (value.includes('--')) return 'A tracking number has no "--".';
  if (!/\d/.test(value)) return 'A tracking number has at least one digit.';
  if (PIECE_END.test(value)) return 'It ends like a piece label (-P1, -P2 …). Use another ending.';
  if (BOX_BARCODE.test(value)) return 'That is a box barcode (EB…), not a tracking number.';
  return null;
}

export function isTrackingNo(value: string): boolean {
  return trackingNoProblem(value) === null;
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, '0');
}

/** The number at position `seq` of a series, in `year` (ignored without a year part). */
export function formatTrackingNumber(
  series: TrackingSeriesFormat,
  seq: number,
  year: number,
): string {
  if (!Number.isInteger(seq) || seq < 0 || seq >= 10 ** series.digits) {
    throw new Error(`Running number must be 0 to ${10 ** series.digits - 1}, got ${seq}`);
  }
  if (series.yearPart && (!Number.isInteger(year) || year < 2000 || year > 2099)) {
    throw new Error(`Year must be between 2000 and 2099, got ${year}`);
  }
  const body = (series.yearPart ? pad(year % 100, 2) : '') + pad(seq, series.digits);
  const check = series.checkDigit ? String(luhnCheckDigit(body)) : '';
  return `${series.prefix}${body}${check}${series.suffix}`;
}

export interface SeriesMatch<S extends TrackingSeries = TrackingSeries> {
  readonly series: S;
  readonly seq: number;
  /** The full year from the year part, or null for a series without one. */
  readonly year: number | null;
}

const escape = (text: string): string => text.replace(/[-]/g, '\\-');

function patternOf(f: TrackingSeriesFormat): RegExp {
  return new RegExp(
    `^${escape(f.prefix)}${f.yearPart ? '(\\d{2})' : '()'}(\\d{${f.digits}})${f.checkDigit ? '(\\d)' : '()'}${escape(f.suffix)}$`,
  );
}

/** Whether `text` is a number of this one series (shape and check digit). */
export function matchSeries(
  f: TrackingSeriesFormat,
  text: string,
): { seq: number; year: number | null } | null {
  const m = patternOf(f).exec(text);
  if (!m) return null;
  const [, yy = '', seq = '', check = ''] = m;
  if (f.checkDigit && luhnCheckDigit(yy + seq) !== Number(check)) return null;
  return { seq: Number(seq), year: f.yearPart ? 2000 + Number(yy) : null };
}

/**
 * Which series a tracking number belongs to, if any. When two could match, the one with the longer
 * prefix and suffix wins (series that could make the same number are refused when saved).
 */
export function parseTrackingNumber<S extends TrackingSeries>(
  series: readonly S[],
  text: string,
): SeriesMatch<S> | null {
  let best: SeriesMatch<S> | null = null;
  let bestWeight = -1;
  for (const s of series) {
    const m = matchSeries(s, text);
    if (!m) continue;
    const weight = s.prefix.length + s.suffix.length;
    if (weight > bestWeight) {
      best = { series: s, ...m };
      bestWeight = weight;
    }
  }
  return best;
}

/** What a series' numbers look like: its first and second number this year. */
export function seriesExamples(
  f: TrackingSeriesFormat,
  startNumber: number,
  year: number,
): string[] {
  const first = Math.max(0, Math.min(startNumber, 10 ** f.digits - 2));
  return [formatTrackingNumber(f, first, year), formatTrackingNumber(f, first + 1, year)];
}

/** Why a series format cannot be used, in words (empty when it can). */
export function seriesFormatProblems(f: TrackingSeriesFormat & { startNumber?: number }): string[] {
  const problems: string[] = [];
  for (const [label, value] of [
    ['prefix', f.prefix],
    ['suffix', f.suffix],
  ] as const) {
    if (!AFFIX.test(value)) problems.push(`The ${label} may use A–Z, 0–9 and "-" only.`);
    if (value.length > SERIES_AFFIX_MAX)
      problems.push(`The ${label} has at most ${SERIES_AFFIX_MAX} characters.`);
  }
  if (/^-P\d*$/.test(f.suffix)) problems.push('The suffix may not look like a piece label (-P1).');
  if (!Number.isInteger(f.digits) || f.digits < SERIES_DIGITS_MIN || f.digits > SERIES_DIGITS_MAX) {
    problems.push(
      `The running number has ${SERIES_DIGITS_MIN} to ${SERIES_DIGITS_MAX} digits, not ${f.digits}.`,
    );
    return problems;
  }
  const start = f.startNumber ?? 1;
  if (!Number.isInteger(start) || start < 1 || start >= 10 ** f.digits - 1) {
    problems.push(`The first number must be from 1 to ${10 ** f.digits - 2}.`);
  }
  if (problems.length > 0) return problems;
  // Every number of the series must be a valid tracking number: try the shortest and longest.
  for (const n of [1, 10 ** f.digits - 1]) {
    const sample = formatTrackingNumber(f, n, 2026);
    const why = trackingNoProblem(sample);
    if (why) {
      problems.push(`Its numbers would not work (${sample}): ${why}`);
      break;
    }
  }
  return problems;
}

/** Positions of a series' numbers: a fixed character, or "any digit". */
function shapeOf(f: TrackingSeriesFormat): (string | null)[] {
  const digitCount = (f.yearPart ? 2 : 0) + f.digits + (f.checkDigit ? 1 : 0);
  return [...f.prefix.split(''), ...Array<null>(digitCount).fill(null), ...f.suffix.split('')];
}

/**
 * Whether two series could ever make the same number (ignoring check digits, which only make it
 * rarer). Two series that overlap would hand out one number twice, so saving the second is refused.
 */
export function formatsOverlap(a: TrackingSeriesFormat, b: TrackingSeriesFormat): boolean {
  const x = shapeOf(a);
  const y = shapeOf(b);
  if (x.length !== y.length) return false;
  return x.every((cx, i) => {
    const cy = y[i];
    if (cx === null && (cy === null || cy === undefined)) return true;
    if (cx === null) return /\d/.test(cy as string);
    if (cy === null || cy === undefined) return /\d/.test(cx);
    return cx === cy;
  });
}

/**
 * Whether something typed into a search box is meant as a tracking number: a valid one with at
 * least one letter too (names have no digits, phone numbers no letters).
 */
export function looksLikeTrackingNo(value: string): boolean {
  return isTrackingNo(value) && /[A-Z]/.test(value) && /\d/.test(value);
}
